<template>
  <main class="app-get-step" id="mainContent">
    <div class="app-main-content" v-if="$store.state.guestLogin.duplicateRegistration && $store.state.guestLogin.duplicateRegistration.descriptions == ''">
      <h1>{{ screenData.title }}</h1>
      <p>Print the confirmation page once you have completed your online transaction.</p>
      <div class="app-content-panels">
        <form class="app-panel-left">
          <p><strong>To process your duplicate request, you will need:</strong></p>
          <ul class="renew-ready">
            <li>
              <p> - A <b>credit card</b></p>
            </li>
            <li>
              <p> - A <b>valid email address</b> where you will receive a copy of your duplicate registration</p>
            </li>
            <li>
              <p>- A <b>printer</b> connected to your computer in order to print your receipt</p>
            </li>
            <li>
              <p> <b>NOTE:</b> There is a minimum <b>service fee</b> of <b>$1.55</b> per transaction, or <b>2.40%</b> for transactions of $65.00 and over. This fee goes to the third party provider, Federal Payments and is in addition to any DMV fees.</p>
            </li>
          </ul>
          <BaseButtonGroup>
            <CustomButton @click="handleRouting(true)" class="resend-code">Previous</CustomButton>
            <CustomButton @click="handleRouting(false)">Next</CustomButton>
            <CustomButton v-if="!disableBtn" @click="handleRouting(true)" class="resend-code"> Cancel and go to profile </CustomButton>
            <CustomButton :class="{ 'disabled': !disableBtn }" :aria-disabled="!disableBtn ? null: 'true'" class="resend-code"
            @click="goToTxnCart(true)" v-if="disableBtn"> Cancel and go to cart</CustomButton>
          </BaseButtonGroup>
        </form>
      </div>
    </div>
    <div class="app-main-content" v-if="$store.state.guestLogin.duplicateRegistration && $store.state.guestLogin.duplicateRegistration.descriptions != ''">
      <div class="app-alert-error">
        <ul class="err-codes">
          <li v-for="(description, i) in $store.state.guestLogin.duplicateRegistration.descriptions" :key="i">
            <p> {{ description }} </p>
            <p class="sr-only">{{ description }}</p>
          </li>
        </ul>             
      </div>
      <BaseButtonGroup>
        <CustomButton @click="handleRouting(true)" class="reg-previous">Previous</CustomButton>
        <CustomButton :class="{ 'disabled': !disableBtn }" :aria-disabled="!disableBtn ? null: 'true'"
          @click="goToTxnCart(true)" v-if="$store.state.guestLogin.cartTransactions && $store.state.guestLogin.cartTransactions.cartTransactions.length > 0">
          Cancel and go to cart 
        </CustomButton>
        <CustomButton @click="handleRouting(true)" v-else> Cancel and go to profile </CustomButton>
      </BaseButtonGroup>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'; 
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import * as COMMONS from '../../store/_commons';
import CustomButton from '../../components/CustomButton.vue';

// Vuex store and router 
const store = useStore();
const router = useRouter();

// Props and Emits
const emit = defineEmits(['getStep']);

// Reactive data 
const stepNum = ref(1);
const screenData = ref({});

// Computed properties
const STEPS = computed(() => store.state.STEPS);
// User type
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);

// Profile route based on user type
const profileRouteName = computed(() => {
  return isBusinessUser.value ? 'BusinessProfile' : 'Profile';
});

const disableBtn = computed(() => {
  let btn = false;
  if (store.state.guestLogin?.cartTransactions) { 
    btn = store.state.guestLogin.cartTransactions.cartTransactions.length > 0;
  }
  return btn;
});

// Methods
const getCreateTransactionService = () => {
  const isEligible = store.state.guestLogin.duplicateRegistration.isEligible;
  store.dispatch(COMMONS.GUEST_CREATETRANSACTION, isEligible);
  // Reset isEligible after passing it
  store.commit('guestLogin/setState', { 
    property: 'duplicateRegistration', 
    value: { ...store.state.guestLogin.duplicateRegistration, isEligible: false } 
  });
};

const getAddTransaction = () => {
  const isEligible = store.state.guestLogin.duplicateRegistration.isEligible;
  store.dispatch(COMMONS.GUEST_ADDTRANSACTION, isEligible);
  // Reset isEligible after passing it
  store.commit('guestLogin/setState', { 
    property: 'duplicateRegistration', 
    value: { ...store.state.guestLogin.duplicateRegistration, isEligible: false } 
  });
};

const handleRouting = (isPrev = false) => {
  if (isPrev) {
    router.push({ name: profileRouteName.value });
  } else {
    if (store.state.guestLogin?.cartTransactions && store.state.guestLogin.cartTransactions.cartTransactions.length > 0) {
      getAddTransaction();
    } else {
      getCreateTransactionService();
    }
    emit('getStep', 2);
  }
};

const goToTxnCart = () => {
  store.state.guestLogin.step = 4;
  emit('getStep', 4);
};

// Lifecycle hooks
onMounted(() => {
  window.scroll(0, 0);
  screenData.value = STEPS.value[stepNum.value]; // Access reactive values with .value
  document.getElementById("mainContent").focus();

  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");
});
</script>


<style scoped lang="scss">
.app-alert-error {
  display: block;
  margin-top: 20px;
  margin-bottom: 2rem;
  font-size: 18px;
  line-height: 28px;
  padding-left: 1rem;
  padding-right: 10px;
  ul {
    padding: 10px;
  }
}
.app-content-panels {
  margin-top: 0px;
  .resend-code{
    background-color: white;
    color: var(--ri-blue);

    &:hover{
      background-color: var(--ri-lighter-blue);
      color: var(--ri-darker-blue);
    }
  }
}
.renew-ready {
  margin-bottom: 2rem;
}
.app-button-group {
  margin-top: 1rem;
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
.err-codes {
  list-style: initial;
  margin-left: 1rem;
}
.reg-previous{
  background-color: white;
  color: var(--ri-blue);

  &:hover{
    background-color: var(--ri-lighter-blue);
    color: var(--ri-darker-blue);
  }
}
</style>
